import { Hub } from 'aws-amplify/utils';

import { HUB_CHANNEL as API_HUB_CHANNEL } from '../api/constants';

import Postmate from 'postmate';
import {
  Account,
  OrganizationMemberStatuses,
  StripeSubscriptionStatus,
} from '../types/Account';
import log, { LogColor, logx } from '../utils/log';
import {
  EVENT_LOGIN_SUCCESS,
  HUB_CHANNEL as EXTENSION_HUB_CHANNEL,
} from './constants';
import { clearInvalidOrigins, setLiveExtensionOrigins } from './redir-state';

const COLOR: LogColor = 'pink';

/** stripe subscription status (DUPED in extension UserTypes.ts) */
export type LoginSubStatus = StripeSubscriptionStatus | '';

/**
 * Login data that we pass to the extension, so
 * it knows the user's state.
 *
 * Example from the extension:
 *
 * ```json
 * {
 *   expires: 1769834032,
 *   status: "active",
 *   updated: 1738298673,
 *   userId: "us-east-1:2d13ada2-f05f-c3b5-c9ab-46faded4d247"
 * }
 * ```
 *
 * NOTE: I think updated is added inside the extension?
 */
type LoginData = {
  userId: string;
  orgId: string;
  expires: number;
  status: LoginSubStatus;
  show_experimental?: boolean;
  sub_created?: number;
};

const loginUser = ({
  postmateChild,
  userId,
  orgId,
  subCreatedUnixTimestamp,
  expiresUnixTimestamp,
  status,
  // TODO - update this web code to pass through info for:
  // 1. how long into subscription, and... (`createdUnixTimestamp`)
  // 2. whether enterprise or not (`account_type` or `orgId`?)
  //
  // then..
  // 1. update extension to handle reading this info
  //    to determine showing the extension modal
  // 2. also update extension to jspdf 3.0.0 and remove the dompurify dep
  //
}: {
  postmateChild: Postmate.ParentAPI;
  userId: string;
  orgId?: string;
  subCreatedUnixTimestamp: number;
  expiresUnixTimestamp: number;
  status: LoginSubStatus;
  showExperimental?: boolean;
}) => {
  // data needs:
  // * token
  // * expires
  // * status

  // TODO - is this partially duplicating work from ExtensionAPIServer.js -> authState call?

  const data: LoginData = {
    userId,
    orgId: orgId || '',
    expires: expiresUnixTimestamp,
    status: status || '',
    sub_created: subCreatedUnixTimestamp,
  };

  // HACK - add show_experimental
  const tokenKey = 'showExperimental';
  const token = window.location.search
    .substring(1)
    .split('&')
    .filter((x) => x === tokenKey || x.startsWith(tokenKey + '='))[0];
  if (token) {
    const sp = token.split('=');
    const tokenVal = sp[1];
    const show_experimental =
      !tokenVal ||
      ['t', '1', 'y'].indexOf(tokenVal.substring(0, 1).toLowerCase()) !== -1;
    data.show_experimental = show_experimental;
  }

  // there is no callback or Promise for Postmate.call
  postmateChild.call('login', data);
};

/**
 * A listener for ExtensionProvider that is instantiated
 * with `postmateProm`, which returns all the handshakes
 * with any installed extensions.
 */
export default class LoginListener {
  postmateProm: Promise<Postmate.ParentAPI[]>;
  unsubscribeHub: () => void;

  constructor(postmateProm: Promise<Postmate.ParentAPI[]>) {
    this.unsubscribeHub = Hub.listen(API_HUB_CHANNEL, this.onHubCapsule);
    this.postmateProm = postmateProm;

    // Update `redir-state` - do extra 1.5 second delay to be
    // extra careful that we got all valid extensions
    window.setTimeout(() => {
      postmateProm
        .then((children) => {
          const origins: string[] = [];

          children.forEach((child) => {
            if (hasChildOrigin(child)) {
              origins.push(child.childOrigin);
            }
          });

          setLiveExtensionOrigins(origins);
          clearInvalidOrigins(origins);
        })
        .catch((err) => {
          log.debug(err);
        });
    }, 1500);
  }

  destroy() {
    this.unsubscribeHub();
  }

  // hub listener
  onHubCapsule = ({
    channel,
    payload,
    source,
  }: {
    channel: string;
    payload: unknown;
    source?: unknown;
  }) => {
    //
    if (channel === API_HUB_CHANNEL) {
      // HACK - fake the types :(
      const _payload = payload as {
        data?: {
          pathPrefix?: string;
          object?: Account;
        };
      };

      const object = _payload.data?.object;
      if (_payload.data?.pathPrefix === 'account' && isAccountLike(object)) {
        logx(
          '[LoginListener.onHubCapsule]',
          { channel, payload, source, object },
          COLOR,
        );

        const sub = object.subscription;
        const userId = object.userId;
        const subCreatedUnixTimestamp = sub?.created?.unix() || 0;
        const expiresUnixTimestamp = sub?.current_period_end?.unix() || 0;
        const status = sub ? sub.status : '';
        // NOTE(orgId): only pass `orgId` when `orgStatus` is "active", since
        // invited/deactivated members keep it (matches getAccountSimple)
        const orgId =
          object.orgStatus === OrganizationMemberStatuses.Active
            ? object.orgId
            : '';

        this.postmateProm
          .then((postmateChildren) => {
            postmateChildren.forEach((postmateChild) => {
              // setup login-success handler - 'login-success' HARDCODED in extension
              // TODO - "once" this, so it doesn't pile up and execute a bunch of times
              postmateChild.on('login-success', (data) => {
                logx(
                  '[LoginListener.onHubCapsule.postmateProm.login-success]',
                  { data },
                  COLOR,
                );
                // status string is from Stripe - using "subOk" logic from extension
                if (data.status && data.status !== 'canceled') {
                  // TODO(multi-handshake): test what happens with multiple dispatches?
                  Hub.dispatch(
                    EXTENSION_HUB_CHANNEL,
                    { event: EVENT_LOGIN_SUCCESS, data },
                    'LoginListener',
                  );
                }
              });

              // execute the remote login call to the extension
              logx(
                '{{update?}}',
                {
                  userId,
                  subCreatedUnixTimestamp,
                  expiresUnixTimestamp,
                  status,
                },
                COLOR,
              );
              loginUser({
                postmateChild,
                userId,
                orgId,
                subCreatedUnixTimestamp,
                expiresUnixTimestamp,
                status,
              });
            });
          })
          .catch((err: unknown) => {
            log.error('[LoginListener.onHubCapsule.postmateProm.error]', err);
          });
      }
    }
  };
}

// ## Helpers

/**
 * Not every 'account' hub event carries an `Account` (e.g., checkout URLs or
 * `{ type, data }` wrappers), and pushing one of those would log the
 * extension out, so only accept objects with a `userId`.
 */
function isAccountLike(object: unknown): object is Account {
  return (
    !!object &&
    typeof object === 'object' &&
    typeof (object as Account).userId === 'string'
  );
}

type ParentAPIWIthChildOrigin = Postmate.ParentAPI & { childOrigin: string };

/**
 * HACK: fix undocumented type on `Postmate.ParentAPI`
 *
 * Also log an error if not present.
 */
function hasChildOrigin(
  child: Postmate.ParentAPI,
): child is ParentAPIWIthChildOrigin {
  const ret =
    typeof (child as ParentAPIWIthChildOrigin).childOrigin === 'string';
  if (!ret) {
    log.error(
      '[LoginListener.hasChildOrigin] missing Postmate.ParentAPI.childOrigin',
    );
  }
  return ret;
}
